// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { OcularMotilityPositionResultsType } from "src/services/exams/types";

const SETTLE_VIEW_SIZE = 148;
const SETTLE_CENTER = SETTLE_VIEW_SIZE / 2;
const SETTLE_OUTER_RADIUS = 68;
// The outer ring's label sits just above it, at the viewBox's very edge --
// extend the viewBox by this much on every side so that label (and its
// font ascender) has room to render without being clipped.
const SETTLE_VIEW_MARGIN = 8;
// Smallest "nice" degree step (doubled, for the outer ring) that still
// comfortably bounds whatever's farthest from center on this card's plot --
// mirrors the reference mockup's per-card autoscale, just without needing
// its exact (unrecoverable from a single sample) rounding rule.
const RING_NICE_STEPS = [0.5, 1, 2, 5, 10, 20, 50];

type EyeImageType = {
  data: string;
  format: string;
};

type OcularMotilityCardType = {
  label: string;
  subtitle: string;
  result?: OcularMotilityPositionResultsType;
  image?: EyeImageType;
};

const FigureRow = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "1px",
      flex: "0 1 calc(33.333% - 11px)",
      minWidth: "110px",
    }}
  >
    <Typography fontSize="12px" color="#5B6D7A">
      {label}
    </Typography>
    <Typography fontSize="12px">{value}</Typography>
    {sub && (
      <Typography fontSize="10px" color="#5B6D7A">
        {sub}
      </Typography>
    )}
  </Box>
);

const OcularMotilityCard = ({
  label,
  subtitle,
  result,
  image,
}: OcularMotilityCardType) => {
  const { t } = useTranslation();

  const settle = useMemo(() => {
    if (!result) return null;

    const {
      targetAngleX,
      targetAngleY,
      leftEyeAngleX,
      leftEyeAngleY,
      rightEyeAngleX,
      rightEyeAngleY,
      leftEyeDeviation,
      rightEyeDeviation,
      leftEyeSpread,
      rightEyeSpread,
    } = result;

    // The spread circle is centered on the (already off-center) eye point,
    // not on the origin -- so its farthest extent from center is the eye's
    // deviation PLUS its spread radius, not either one alone. Autoscaling
    // on just the max of the two (as opposed to their sum) let a wide
    // spread circle poke outside the outer ring and get clipped.
    const maxDeg = Math.max(
      leftEyeDeviation + leftEyeSpread,
      rightEyeDeviation + rightEyeSpread,
      0.5
    );
    const step =
      RING_NICE_STEPS.find((candidate) => candidate * 2 >= maxDeg) ??
      RING_NICE_STEPS[RING_NICE_STEPS.length - 1];
    const scale = SETTLE_OUTER_RADIUS / (step * 2);

    return {
      step,
      innerRadius: step * scale,
      outerRadius: step * 2 * scale,
      leftX: SETTLE_CENTER + (leftEyeAngleX - targetAngleX) * scale,
      leftY: SETTLE_CENTER - (leftEyeAngleY - targetAngleY) * scale,
      rightX: SETTLE_CENTER + (rightEyeAngleX - targetAngleX) * scale,
      rightY: SETTLE_CENTER - (rightEyeAngleY - targetAngleY) * scale,
      leftSpreadR: leftEyeSpread * scale,
      rightSpreadR: rightEyeSpread * scale,
    };
  }, [result]);

  const signed = (value: number) =>
    `${value >= 0 ? "+" : ""}${value.toFixed(2)}°`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "8px",
        border: "1px solid #000",
        borderRadius: "4px",
        flex: "1 1 350px",
        minWidth: "350px",
      }}
    >
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        gap="8px"
      >
        <Box>
          <Typography fontSize="14px" fontWeight={600}>
            {t(label)}
          </Typography>
          <Typography fontSize="12px" color="#5B6D7A">
            {t(subtitle)}
          </Typography>
        </Box>
        {result && (
          <Typography fontSize="12px" color="#5B6D7A" whiteSpace="nowrap">
            {result.targetAngleX.toFixed(2)}°, {result.targetAngleY.toFixed(2)}°
          </Typography>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap="8px">
        <Box
          sx={{
            position: "relative",
            borderRadius: "4px",
            overflow: "hidden",
            backgroundColor: "#5B6D7A",
            // Keeps the box's shape fixed at 2:1 no matter how wide the
            // card itself gets, instead of a fixed pixel height that made a
            // wider card's box relatively shorter and force a tighter crop.
            aspectRatio: "2 / 1",
            maxWidth: "400px",
            width: "100%",
            alignSelf: "center",
          }}
        >
          {image ? (
            <img
              src={`data:image/${image.format?.toLowerCase?.() || "jpeg"};base64,${image.data}`}
              alt={t("{{label}} eye capture", { label: t(label) })}
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#FFF",
                fontSize: "12px",
                textAlign: "center",
                p: "12px",
              }}
            >
              {t("No image available.")}
            </Box>
          )}
          {image && (
            <>
              <Typography
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  px: "6px",
                  py: "2px",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  color: "#FFF",
                  backgroundColor: "#000",
                  borderRadius: "4px",
                }}
              >
                {t("Right Eye")}
              </Typography>
              <Typography
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  px: "6px",
                  py: "2px",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  color: "#FFF",
                  backgroundColor: "#000",
                  borderRadius: "4px",
                }}
              >
                {t("Left Eye")}
              </Typography>
            </>
          )}
        </Box>

        {result && settle && (
          <svg
            viewBox={`${-SETTLE_VIEW_MARGIN} ${-SETTLE_VIEW_MARGIN} ${SETTLE_VIEW_SIZE + SETTLE_VIEW_MARGIN * 2} ${SETTLE_VIEW_SIZE + SETTLE_VIEW_MARGIN * 2}`}
            width="200"
            height="200"
            role="img"
            aria-label={t("Where each eye sat relative to the target")}
            style={{ flexShrink: 0, alignSelf: "center" }}
          >
            <circle
              cx={SETTLE_CENTER}
              cy={SETTLE_CENTER}
              r={settle.innerRadius}
              fill="none"
              stroke="#5B6D7A40"
            />
            <text
              x={SETTLE_CENTER + 2}
              y={SETTLE_CENTER - settle.innerRadius - 2}
              fontSize="10"
              fill="#5B6D7A"
            >
              {settle.step}°
            </text>
            <circle
              cx={SETTLE_CENTER}
              cy={SETTLE_CENTER}
              r={settle.outerRadius}
              fill="none"
              stroke="#5B6D7A40"
            />
            <text
              x={SETTLE_CENTER + 2}
              y={SETTLE_CENTER - settle.outerRadius - 2}
              fontSize="10"
              fill="#5B6D7A"
            >
              {settle.step * 2}°
            </text>
            <line
              x1={0}
              y1={SETTLE_CENTER}
              x2={SETTLE_VIEW_SIZE}
              y2={SETTLE_CENTER}
              stroke="#5B6D7A40"
            />
            <line
              x1={SETTLE_CENTER}
              y1={0}
              x2={SETTLE_CENTER}
              y2={SETTLE_VIEW_SIZE}
              stroke="#5B6D7A40"
            />
            <line
              x1={settle.leftX}
              y1={settle.leftY}
              x2={settle.rightX}
              y2={settle.rightY}
              stroke="#E2772E"
              strokeDasharray="3 3"
            />
            <circle
              cx={settle.leftX}
              cy={settle.leftY}
              r={settle.leftSpreadR}
              fill="none"
              stroke="red"
              opacity={0.5}
            />
            <circle
              cx={settle.rightX}
              cy={settle.rightY}
              r={settle.rightSpreadR}
              fill="none"
              stroke="blue"
              opacity={0.5}
            />
            <circle cx={settle.leftX} cy={settle.leftY} r={4} fill="red" />
            <circle cx={settle.rightX} cy={settle.rightY} r={4} fill="blue" />
            <path
              d={`M${SETTLE_CENTER - 6} ${SETTLE_CENTER}h12M${SETTLE_CENTER} ${SETTLE_CENTER - 6}v12`}
              stroke="#5B6D7A"
              strokeWidth={1.5}
            />
          </svg>
        )}
      </Box>

      {result && settle ? (
        <Box display="flex" flexDirection="column" gap="8px" minWidth={0}>
          <Box display="flex" flexWrap="wrap" gap="8px">
            <FigureRow
              label={t("Misalignment")}
              value={`${result.misalignment.toFixed(2)}°`}
              sub={`H ${signed(result.horizontalMisalignment)} · V ${signed(result.verticalMisalignment)}`}
            />
            <FigureRow
              label={t("From Primary")}
              value={`${result.misalignmentFromPrimary.toFixed(2)}°`}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap="8px">
            <FigureRow
              label={t("Deviation")}
              value={
                <>
                  <Box component="span" color="red">
                    {result.leftEyeDeviation.toFixed(2)}°
                  </Box>{" "}
                  <Box component="span" color="blue">
                    {result.rightEyeDeviation.toFixed(2)}°
                  </Box>
                </>
              }
              sub={`${t("left")} · ${t("right")}`}
            />
            <FigureRow
              label={t("Excursion")}
              value={
                <>
                  <Box component="span" color="red">
                    {result?.leftEyeExcursion
                      ? (result?.leftEyeExcursion * 100).toFixed(2)
                      : "—"}
                    {result?.leftEyeExcursion && "%"}
                  </Box>{" "}
                  <Box component="span" color="blue">
                    {result?.rightEyeExcursion
                      ? (result?.rightEyeExcursion * 100).toFixed(2)
                      : "—"}
                    {result?.rightEyeExcursion && "%"}
                  </Box>
                </>
              }
              sub={t("of the angle asked for")}
            />
            <FigureRow
              label={t("Spread")}
              value={
                <>
                  <Box component="span" color="red">
                    {result.leftEyeSpread.toFixed(2)}°
                  </Box>{" "}
                  <Box component="span" color="blue">
                    {result.rightEyeSpread.toFixed(2)}°
                  </Box>
                </>
              }
              sub={t("how still it was held")}
            />
          </Box>
        </Box>
      ) : (
        <Typography fontSize="12px" color="#5B6D7A">
          {t("Not measured.")}
        </Typography>
      )}

      {result && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap="8px"
          color="#5B6D7A"
          fontSize="12px"
        >
          <span>{t("{{count}} sample", { count: result.sampleCount })}</span>
          <span>
            {t("{{count}} discarded", { count: result.closedEyeSampleCount })}
          </span>
          <span>
            {t("{{value}}s hold", { value: result.holdDuration.toFixed(1) })}
          </span>
        </Box>
      )}
    </Box>
  );
};

export default OcularMotilityCard;
